import * as z from 'zod';
import { digitalCatalogSource } from './catalogSourceStorage';

const currencyIsoSchema = z.union([
  z.literal('EUR'),
  z.literal('USD'),
  z.literal('GBP'),
  z.literal('CHF'),
  z.literal('HUF'),
  z.literal('JPY')
]);

export const eCommerceEventIdentitySchema = z.object({
  anonymousId: z.string().nonempty(),
  authenticatedId: z.string().nullable()
});

export type ECommerceEventIdentity = z.infer<typeof eCommerceEventIdentitySchema>;

// Digital catalog attribution (ECOM-5701), set only while a catalog entry is persisted.
const catalogSourceFields = {
  source: z.literal(digitalCatalogSource).optional()
};

const commonECommerceEventSchema = eCommerceEventIdentitySchema.extend({
  event: z.literal('ga4_event_ecommerce')
});

export const eCommerceResetEventSchema = z.object({
  event_name: z.literal('ecommerce_reset'),
  ecommerce: z.null()
});

export type ECommerceResetEvent = z.infer<typeof eCommerceResetEventSchema>;

const commonItemSchema = z.object({
  item_id: z.string().nonempty(),
  item_name: z.string().nonempty(),
  item_brand: z.string().nonempty(),
  price: z.number().positive(),
  item_list_name: z.string().nonempty(),
  item_list_id: z.string().nonempty(),

  discount: z.number().positive().optional(),
  item_category: z.string().nonempty().optional(),
  item_category2: z.string().nonempty().optional(),
  item_category3: z.string().nonempty().optional(),
  item_category4: z.string().nonempty().optional(),
  item_category5: z.string().nonempty().optional(),

  item_is_new: z.boolean(),
  item_is_sale: z.boolean(),
  item_is_out_of_stock: z.boolean(),
  item_is_preorder: z.boolean()
});

export type CommonECommerceEventItem = z.infer<typeof commonItemSchema>;

const viewItemEventItemSchema = commonItemSchema.extend({
  quantity: z.number().int().positive(),
  // view_item omits list attribution on miss (AC5/NFR-FT-003): make it optional here so an
  // omitted field validates cleanly rather than fail-open logging. On-PLP events keep the
  // required commonItemSchema fields (AC9 — unchanged).
  item_list_id: z.string().nonempty().optional(),
  item_list_name: z.string().nonempty().optional()
});

export type ViewItemEventItem = z.infer<typeof viewItemEventItemSchema>;

const viewItemECommerceSchema = z.object({
  currency: currencyIsoSchema,
  value: z.number().positive(),
  // Event-level attribution — present only when the item carries list context (AC4); omitted on miss.
  item_list_id: z.string().nonempty().optional(),
  item_list_name: z.string().nonempty().optional(),
  items: z.tuple([viewItemEventItemSchema])
});

export const viewItemEventSchema = commonECommerceEventSchema.extend({
  event_name: z.literal('view_item'),
  ecommerce: viewItemECommerceSchema
});

export type ViewItemEvent = z.infer<typeof viewItemEventSchema>;

const addToWishlistEventItemSchema = commonItemSchema.extend({
  index: z.number().int().nonnegative(),
  quantity: z.literal(1),
  // add_to_wishlist omits list attribution on miss (same convention as view_item).
  item_list_id: z.string().nonempty().optional(),
  item_list_name: z.string().nonempty().optional()
});
export type AddToWishlistEventItem = z.infer<typeof addToWishlistEventItemSchema>;

const addToWishlistECommerceSchema = z.object({
  currency: currencyIsoSchema,
  value: z.number().positive(),
  // Event-level attribution — present only when the item carries list context (AC4); omitted on miss.
  item_list_id: z.string().nonempty().optional(),
  item_list_name: z.string().nonempty().optional(),
  items: z.tuple([addToWishlistEventItemSchema])
});

export const addToWishlistEventSchema = commonECommerceEventSchema.extend({
  event_name: z.literal('add_to_wishlist'),
  ecommerce: addToWishlistECommerceSchema
});
export type AddToWishlistEvent = z.infer<typeof addToWishlistEventSchema>;

const itemListEventItemSchema = commonItemSchema.extend({
  index: z.number().int().nonnegative()
});

const addToCartEventItemSchema = commonItemSchema.extend({
  quantity: z.number().int().positive(),
  index: z.number().int().nonnegative(),
  // List attribution is omitted (not emptied) on miss, mirroring view_item (NFR-FT-003).
  item_list_id: z.string().optional(),
  item_list_name: z.string().optional()
});
export type AddToCartEventItem = z.infer<typeof addToCartEventItemSchema>;

const addToCartECommerceSchema = z.object({
  ...catalogSourceFields,
  currency: currencyIsoSchema,
  value: z.number().nonnegative(),
  items: z.array(addToCartEventItemSchema).min(1)
});

export const addToCartEventSchema = commonECommerceEventSchema.extend({
  event_name: z.literal('add_to_cart'),
  ecommerce: addToCartECommerceSchema
});
export type AddToCartEvent = z.infer<typeof addToCartEventSchema>;

const viewCartEventItemSchema = commonItemSchema.extend({
  affiliation: z.string().nonempty(),
  index: z.number().int().nonnegative(),
  quantity: z.number().int().positive(),
  price: z.number().nonnegative(),
  item_list_id: z.string().nonempty().optional(),
  item_list_name: z.string().nonempty().optional()
});
export type ViewCartEventItem = z.infer<typeof viewCartEventItemSchema>;

const viewCartECommerceSchema = z.object({
  ...catalogSourceFields,
  currency: currencyIsoSchema,
  value: z.number().nonnegative(),
  items: z.array(viewCartEventItemSchema)
});
export type ViewCartECommerce = z.infer<typeof viewCartECommerceSchema>;

export const viewCartEventSchema = commonECommerceEventSchema.extend({
  event_name: z.literal('view_cart'),
  ecommerce: viewCartECommerceSchema
});
export type ViewCartEvent = z.infer<typeof viewCartEventSchema>;

export type ItemListEventItem = z.infer<typeof itemListEventItemSchema>;

const indexIsIncremental = (items: readonly ItemListEventItem[]): boolean =>
  items.every((item, index) => {
    if (index === 0) {
      return true;
    }

    return item.index === items[index - 1].index + 1;
  });

const itemListEcommerceSchema = z.object({
  items: z.array(itemListEventItemSchema).refine(indexIsIncremental, {
    message: 'index must be incremental without gaps'
  })
});

const viewItemListECommerceSchema = itemListEcommerceSchema.extend({
  item_list_name: z.string().nonempty(),
  item_list_id: z.string().nonempty()
});

export const viewItemListEventSchema = commonECommerceEventSchema.extend({
  event_name: z.literal('view_item_list'),
  ecommerce: viewItemListECommerceSchema
});

export type ViewItemListEvent = z.infer<typeof viewItemListEventSchema>;

// select_item mirrors view_item_list's payload shape (ECOM-5811): same list attribution + items.
export const selectItemEventSchema = commonECommerceEventSchema.extend({
  event_name: z.literal('select_item'),
  ecommerce: itemListEcommerceSchema
});

export type SelectItemEvent = z.infer<typeof selectItemEventSchema>;

export const shippingTierSchema = z.union([z.literal('HD'), z.literal('CC'), z.literal('CC-HD')]);
const paymentTypeSchema = z.union([
  z.literal('KLARNA'),
  z.literal('AMAZON_PAY'),
  z.literal('PAYPAL'),
  z.literal('PAY_AT_STORE')
]);

const customerTypeSchema = z.union([z.literal('new'), z.literal('returning'), z.literal('guest')]);

const purchaseEventItemSchema = commonItemSchema.extend({
  affiliation: z.string().nonempty(),
  shipping_tier: z.union([z.literal('HD'), z.literal('CC')]),
  index: z.number().int().nonnegative(),
  coupon: z.string().nonempty().optional(),
  item_list_id: z.string().nonempty().optional(),
  item_list_name: z.string().nonempty().optional(),
  price: z.number().nonnegative(),
  quantity: z.number().int().positive()
});

export type PurchaseEventItem = z.infer<typeof purchaseEventItemSchema>;

const purchaseECommerceSchema = z.object({
  ...catalogSourceFields,
  currency: currencyIsoSchema,
  value: z.number().nonnegative(),
  transaction_id: z.string().nonempty(),
  shipping: z.number().nonnegative(),
  tax: z.number().nonnegative(),
  shipping_tier: shippingTierSchema,
  coupon: z.string().nonempty().optional(),
  payment_type: paymentTypeSchema,
  customer_type: customerTypeSchema,
  // Event-level attribution — set only when all items share one list (AC4); omitted otherwise.
  item_list_id: z.string().nonempty().optional(),
  item_list_name: z.string().nonempty().optional(),
  items: z.array(purchaseEventItemSchema).min(1)
});

export const purchaseEventSchema = commonECommerceEventSchema.extend({
  event_name: z.literal('purchase'),
  ecommerce: purchaseECommerceSchema
});

export type PurchaseEvent = z.infer<typeof purchaseEventSchema>;

const beginCheckoutEventItemSchema = commonItemSchema.extend({
  affiliation: z.string().nonempty(),
  shipping_tier: z.union([z.literal('HD'), z.literal('CC')]),
  index: z.number().int().nonnegative(),
  quantity: z.number().int().positive(),
  coupon: z.string().nonempty().optional(),
  price: z.number().nonnegative(),
  item_list_id: z.string().nonempty().optional(),
  item_list_name: z.string().nonempty().optional()
});
export type BeginCheckoutEventItem = z.infer<typeof beginCheckoutEventItemSchema>;

const beginCheckoutECommerceSchema = z.object({
  ...catalogSourceFields,
  currency: currencyIsoSchema,
  value: z.number().nonnegative(),
  shipping_tier: shippingTierSchema,
  coupon: z.string().nonempty().optional(),
  items: z.array(beginCheckoutEventItemSchema).min(1)
});
export type BeginCheckoutECommerce = z.infer<typeof beginCheckoutECommerceSchema>;

export const beginCheckoutEventSchema = commonECommerceEventSchema.extend({
  event_name: z.literal('begin_checkout'),
  ecommerce: beginCheckoutECommerceSchema
});
export type BeginCheckoutEvent = z.infer<typeof beginCheckoutEventSchema>;

export const userDataEventSchema = z.object({
  event: z.literal('user_data'),
  user_email: z.email()
});

export type UserDataEvent = z.infer<typeof userDataEventSchema>;
